import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Container, Paper, Typography } from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { PublicFooter } from '../maintenances/public';
import { theme } from '../maintenances/public/theme';

const publicAxios = axios.create({
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' },
});

// One-tap opt-out page, reached from the short /m/<token> link in reminder
// SMS/emails. Fires immediately on mount — one tap really is enough.
// Preview mode (`previewData` given, from the rule dialog's message preview): shows the
// success state for the sample data and never calls the API.
function PublicReminderOptOutPage({ previewData = null, previewLanguage = null }) {
  const { token } = useParams();
  const isPreview = previewData !== null;
  const { t } = useTranslation('translation', isPreview && previewLanguage ? { lng: previewLanguage } : undefined);
  const [loading, setLoading] = useState(!isPreview);
  const [workshopName, setWorkshopName] = useState(previewData?.workshop_name || '');
  const [error, setError] = useState(false);

  useEffect(() => {
    if (isPreview) {
      setWorkshopName(previewData?.workshop_name || '');
      return;
    }
    const optOut = async () => {
      try {
        const response = await publicAxios.get(`/api/public/reminders/opt-out/${token}/`);
        setWorkshopName(response.data?.workshop_name || '');
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    optOut();
  }, [token, isPreview, previewData]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: theme.colors.background }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box data-testid="public-reminder-opt-out-page" sx={{ minHeight: isPreview ? '100%' : '100vh', bgcolor: theme.colors.background, display: 'flex', flexDirection: 'column' }}>
      <Box component="header" sx={{ backgroundColor: theme.colors.primary, padding: '16px 20px', color: 'white' }}>
        <Box sx={{ maxWidth: '600px', margin: '0 auto' }}>
          <Typography sx={{ fontSize: '20px', fontWeight: '700' }}>
            {t('public_reminders.title')}
          </Typography>
        </Box>
      </Box>

      <Container maxWidth="sm" sx={{ py: 6, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper
          elevation={0}
          data-testid="opt-out-result"
          sx={{ p: 4, borderRadius: theme.radius.xl, boxShadow: theme.shadows.md, textAlign: 'center', width: '100%' }}
        >
          {!error ? (
            <>
              <Typography sx={{ fontSize: '48px', mb: 2 }}>&#10003;</Typography>
              <Typography sx={{ fontSize: '18px', fontWeight: 600, color: theme.colors.text, mb: 1 }}>
                {t('public_reminders.opt_out_success_title')}
              </Typography>
              <Typography sx={{ fontSize: '14px', color: theme.colors.textMuted }}>
                {workshopName
                  ? t('public_reminders.opt_out_success_message_named', { workshop: workshopName })
                  : t('public_reminders.opt_out_success_message')}
              </Typography>
            </>
          ) : (
            <Typography sx={{ fontSize: '18px', fontWeight: 600, color: theme.colors.text, mb: 1 }}>
              {t('public_reminders.opt_out_error')}
            </Typography>
          )}
        </Paper>
      </Container>

      <PublicFooter />
    </Box>
  );
}

export default PublicReminderOptOutPage;
